<template>
    <div class="modal fade show modal-style" aria-labelledby="orderActionTitle">
        <div class="modal-dialog modal-lg modal-dialog-centered" ref="modalRef">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                            <h4 class="mb-0">Order Action — {{ order.bill_number || order.order_number }}</h4>
                            <button
                                @click="onClickClose"
                                type="button"
                                class="btn-close"
                                aria-label="Close"
                            ></button>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-1">

                <div class="px-2 pb-2">
                    <div class="mb-1">
                        <small class="text-muted">Current Status:</small>
                        <span class="badge bg-info ms-1">{{ currentStatusLabel }}</span>
                    </div>

                    <div v-if="isReadOnly" class="alert alert-secondary py-1">
                        This order is {{ currentStatusLabel }} and is read-only.
                    </div>

                    <div v-if="!isReadOnly">
                        <label class="form-label">Select Action</label>
                        <select class="form-select" v-model="selectedAction" :disabled="forcedMode">
                            <option :value="null" disabled>Select an action</option>
                            <option
                                v-for="action in availableActions"
                                :key="action.key"
                                :value="action.key"
                            >{{ action.label }}</option>
                        </select>

                        <div v-if="isDeliveryAgentAction" class="mt-1">
                            <label class="form-label">Delivery Agent</label>
                            <AsyncSelect
                                placeholder="Select Delivery Agent"
                                v-model="deliveryAgent"
                                :api-service="fetchContactProfiles"
                                :additional-query="deliveryAgentOption"
                            />
                        </div>

                        <div class="mt-1">
                            <label class="form-label">{{ noteLabel }}</label>
                            <textarea
                                class="form-control"
                                rows="3"
                                v-model="note"
                                :placeholder="noteLabel"
                            ></textarea>
                            <span v-if="noteRequired" class="text-danger">
                                <small>{{ noteLabel }} is required</small>
                            </span>
                        </div>

                        <div class="d-flex justify-content-end gap-1 mt-2">
                            <button
                                @click="onClickClose"
                                class="btn btn-outline-secondary"
                            >Cancel</button>
                            <button
                                :disabled="loading || !selectedAction"
                                @click="onSubmit"
                                class="btn btn-primary"
                            >Save</button>
                        </div>
                    </div>

                    <hr class="my-2">

                    <h5 class="mb-2"><i class="fas fa-clock-rotate-left me-1 text-primary"></i> Activity Log</h5>
                    <div v-if="logLoading" class="text-muted"><small>Loading log...</small></div>
                    <div v-if="!logLoading && !hasLogs" class="text-center text-muted py-3">
                        <i class="fas fa-inbox d-block mb-1" style="font-size: 22px; opacity: 0.4;"></i>
                        <small>No activity yet.</small>
                    </div>
                    <div v-if="!logLoading && hasLogs" class="order-timeline">
                        <div
                            v-for="log in logs"
                            :key="log.id"
                            class="timeline-item"
                            :class="{ 'is-inactive': log.record_status === 'inactive' }"
                        >
                            <span class="timeline-dot"><i :class="logIcon(log)"></i></span>
                            <div class="timeline-card">
                                <div class="d-flex justify-content-between align-items-start">
                                    <div>
                                        <span class="timeline-type">{{ logTypeLabel(log) }}</span>
                                        <strong v-if="log.status" class="timeline-title">{{ statusLabel(log.status) }}</strong>
                                        <span
                                            v-if="log.tag_name"
                                            class="timeline-title"
                                            :class="{ 'text-danger text-decoration-line-through': log.record_status === 'inactive' }"
                                        >{{ log.tag_name }}</span>
                                    </div>
                                    <small class="timeline-date">{{ formatLogDate(log.created_at) }}</small>
                                </div>
                                <div v-if="log.delivery_agent_name" class="timeline-meta">
                                    <i class="fas fa-truck me-1"></i>{{ log.delivery_agent_name }}
                                </div>
                                <div v-if="log.note" class="timeline-note">{{ log.note }}</div>
                                <div class="timeline-user"><i class="fas fa-user me-1"></i>{{ log.created_by_name }}</div>
                            </div>
                        </div>
                        <div v-if="hasMoreLogs" class="text-center mt-2">
                            <button class="btn btn-sm btn-outline-primary" :disabled="logLoadingMore" @click="loadMoreLogs">
                                <i class="fas fa-chevron-down me-1"></i>
                                {{ logLoadingMore ? 'Loading...' : 'Load More' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, inject } from 'vue'
import { useRoute } from 'vue-router'
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue'
import handlePurchase from '@/services/modules/purchase'
import handleContact from '@/services/modules/contact'

const props = defineProps({
    order: { type: Object, required: true },
    workflowStatuses: { type: Array, required: true },
    workflowTransitions: { type: Object, required: true },
    forcedStatus: { type: String, default: null }
})

const emit = defineEmits(['close', 'updated'])

const route = useRoute()
const showError = inject('showError')
const showSuccess = inject('showSuccess')

const {
    updateEcommerceOrderStatus,
    getEcommerceOrderActivity
} = handlePurchase()
const { fetchContactProfiles } = handleContact()

const loading = ref(false)
const selectedAction = ref(null)
const note = ref('')
const deliveryAgent = ref(null)
const logs = ref([])
const logLoading = ref(false)
const logLoadingMore = ref(false)
const logPageSize = 3
const logCurrentPage = ref(1)
const logLastPage = ref(1)
const deliveryAgentOption = ref({
    roles: 'delivery_agent'
})

const companyId = computed(() => route.params.companyId)

const noteRequiredStatuses = ['on_hold', 'cancelled', 'sales_return', 'delivered_cancelled']
const noteLabels = {
    on_hold: 'Hold Reason',
    cancelled: 'Cancel Reason',
    sales_return: 'Return Note',
    delivered_cancelled: 'Cancel Reason'
}

const allowedStatuses = computed(() => {
    if (!props.workflowTransitions[props.order.order_status]) return []
    return props.workflowTransitions[props.order.order_status]
})

const forcedMode = computed(() => Boolean(props.forcedStatus))

const viewOnlyStatuses = ['stock_in_transit', 'delivered']

const isReadOnly = computed(() => {
    if (forcedMode.value) return false
    if (viewOnlyStatuses.includes(props.order.order_status)) return true
    return allowedStatuses.value.length === 0
})

const availableActions = computed(() => {
    return allowedStatuses.value.map(status => ({
        key: status,
        label: statusLabel(status),
        type: 'status',
        status: status
    }))
})

const activeAction = computed(() => {
    if (!selectedAction.value) return null
    return availableActions.value.find(action => action.key === selectedAction.value)
})

const statusLabel = (status) => {
    const match = props.workflowStatuses.find(item => item.value === status)
    if (!match) return status
    return match.name
}

const currentStatusLabel = computed(() => statusLabel(props.order.order_status))

const isDeliveryAgentAction = computed(() => {
    if (!activeAction.value) return false
    return activeAction.value.status === 'stock_transfer_pending'
})

const noteRequired = computed(() => {
    if (!activeAction.value) return false
    return noteRequiredStatuses.includes(activeAction.value.status)
})

const noteLabel = computed(() => {
    if (!activeAction.value) return 'Note'
    if (noteLabels[activeAction.value.status]) return noteLabels[activeAction.value.status]
    return 'Note'
})

const hasLogs = computed(() => logs.value.length > 0)

const hasMoreLogs = computed(() => logCurrentPage.value < logLastPage.value)

const logTypeLabels = {
    stage: 'Status',
    delivery_agent: 'Delivery Agent',
    note: 'Note',
    tag: 'Tag'
}

const logTypeLabel = (log) => {
    if (!logTypeLabels[log.type]) return log.type
    return logTypeLabels[log.type]
}

const logIcons = {
    stage: 'fas fa-flag',
    delivery_agent: 'fas fa-truck',
    note: 'fas fa-note-sticky',
    tag: 'fas fa-tag'
}

const logIcon = (log) => {
    if (!logIcons[log.type]) return 'fas fa-circle'
    return logIcons[log.type]
}

const formatLogDate = (value) => {
    if (!value) return ''
    return new Date(value).toLocaleString()
}

const logQuery = (page) => `?company_id=${companyId.value}&offset=${logPageSize}&page=${page}`

const getLogs = async () => {
    logLoading.value = true
    const res = await getEcommerceOrderActivity(props.order.id, logQuery(1))
    logLoading.value = false
    if (!res.status || !res.data) return
    logs.value = res.data.data
    logCurrentPage.value = res.data.current_page
    logLastPage.value = res.data.last_page
}

const loadMoreLogs = async () => {
    if (logLoadingMore.value) return
    logLoadingMore.value = true
    const nextPage = logCurrentPage.value + 1
    const res = await getEcommerceOrderActivity(props.order.id, logQuery(nextPage))
    logLoadingMore.value = false
    if (!res.status || !res.data) return
    logs.value = logs.value.concat(res.data.data)
    logCurrentPage.value = res.data.current_page
    logLastPage.value = res.data.last_page
}

const validate = () => {
    if (!activeAction.value) return false
    if (noteRequired.value && !note.value) {
        showError(`${noteLabel.value} is required`)
        return false
    }
    if (isDeliveryAgentAction.value && !deliveryAgent.value) {
        showError('Please select a delivery agent')
        return false
    }
    return true
}

const runAction = async () => {
    const payload = {
        company_id: companyId.value,
        order_status: activeAction.value.status,
        note: note.value
    }
    if (isDeliveryAgentAction.value && deliveryAgent.value) {
        payload.delivery_agent_id = deliveryAgent.value.id
    }
    return updateEcommerceOrderStatus(props.order.id, payload)
}

const onSubmit = async () => {
    if (!validate()) return
    loading.value = true
    try {
        const res = await runAction()
        loading.value = false
        if (!res.status) {
            return showError(res.message)
        }
        showSuccess(res.message)
        selectedAction.value = null
        note.value = ''
        deliveryAgent.value = null
        await getLogs()
        emit('updated')
    } catch (err) {
        loading.value = false
        showError('Something went wrong, please try again.')
    }
}

const onClickClose = () => {
    emit('close')
}

onMounted(() => {
    if (forcedMode.value) selectedAction.value = props.forcedStatus
    getLogs()
})
</script>

<style scoped>
.modal-style {
    display: block;
    background: #1c1d1c33;
}
.order-timeline {
    position: relative;
    padding-left: 6px;
    max-height: 320px;
    overflow-y: auto;
}
.timeline-item {
    position: relative;
    padding-left: 28px;
    padding-bottom: 14px;
}
.timeline-item::before {
    content: '';
    position: absolute;
    left: 9px;
    top: 20px;
    bottom: -2px;
    width: 2px;
    background: #ebe9f1;
}
.timeline-item:last-child::before {
    display: none;
}
.timeline-dot {
    position: absolute;
    left: 0;
    top: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #7367f0;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 9px;
}
.timeline-item.is-inactive .timeline-dot {
    background: #ea5455;
}
.timeline-card {
    background: #f8f8fb;
    border: 1px solid #ebe9f1;
    border-radius: 8px;
    padding: 8px 12px;
}
.timeline-type {
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #7367f0;
    background: #e7e6f7;
    border-radius: 4px;
    padding: 1px 6px;
    margin-right: 6px;
}
.timeline-title {
    font-size: 13px;
    color: #5e5873;
}
.timeline-date {
    color: #a8a5b3;
    font-size: 11px;
    white-space: nowrap;
}
.timeline-meta {
    font-size: 12px;
    color: #6e6b7b;
    margin-top: 4px;
}
.timeline-note {
    font-size: 12px;
    color: #6e6b7b;
    background: #fff;
    border-left: 2px solid #d8d6e3;
    padding: 3px 8px;
    margin-top: 5px;
    border-radius: 0 4px 4px 0;
}
.timeline-user {
    font-size: 11px;
    color: #a8a5b3;
    margin-top: 5px;
}
</style>
